import { useEffect, useMemo, useRef, useState } from 'react';
import BadgeArt from './BadgeArt';
import { ACHIEVEMENT_CARDS, BADGE_FAMILIES, TIER_LABELS, currentTierBadges, familyStatus } from '../../utils/badges';

const SEEN_KEY = (uid) => `padel-mvp-badges-seen:${uid || 'anon'}`;

function readSeen(uid) {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(SEEN_KEY(uid)) || '[]'));
  } catch (_) {
    return new Set();
  }
}

function writeSeen(uid, ids) {
  try {
    window.localStorage.setItem(SEEN_KEY(uid), JSON.stringify([...ids]));
  } catch (_) {
    // Storage blocked: badges still work, the unlock shine just plays again next time
  }
}

/** Badges not shown on this device before get the unlock shine once. */
function useNewBadges(uid, unlocked, loading) {
  const [fresh, setFresh] = useState(() => new Set());
  useEffect(() => {
    if (loading) return;
    const seen = readSeen(uid);
    const ids = Object.keys(unlocked);
    const newly = ids.filter((id) => !seen.has(id));
    if (newly.length === 0) return;
    setFresh(new Set(newly));
    writeSeen(uid, new Set([...seen, ...ids]));
  }, [uid, unlocked, loading]);
  return fresh;
}

/** Profile "Badges" tile: the current tier of each family the player has reached bronze in. */
export function BadgesTile({ badges, uid, onOpen }) {
  const { unlocked = {}, counts = {}, loading } = badges || {};
  const fresh = useNewBadges(uid, unlocked, loading);
  const shown = useMemo(() => currentTierBadges(counts), [counts]);

  return (
    <button type="button" className="profile-stat profile-badges pc-anim-tile" style={{ '--pc-d': 3 }} onClick={onOpen}>
      <span className="profile-stat__label">Badges</span>
      {shown.length > 0 ? (
        <span className="profile-badges__row">
          {shown.map((badge) => (
            <BadgeArt key={badge.id} badge={badge} isNew={fresh.has(badge.id)} />
          ))}
        </span>
      ) : (
        <span className="profile-badges__empty">{loading ? 'Checking your matches…' : 'Win matches to earn your first badge'}</span>
      )}
      <span className="profile-badges__count">{`${shown.length} of ${BADGE_FAMILIES.length} unlocked`}</span>
    </button>
  );
}

/** Achievements: one card per family with progress to the next tier (Figma 852:3353). */
export function BadgesList({ badges, uid, focusId }) {
  const { unlocked = {}, counts = {}, loading } = badges || {};
  const fresh = useNewBadges(uid, unlocked, loading);
  const cardRefs = useRef({});

  // VIEW on the achievement dialog lands on that badge's card
  const focusFamily = focusId ? focusId.split('-')[0] : null;
  useEffect(() => {
    const el = focusFamily && cardRefs.current[focusFamily];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusFamily]);

  return (
    <ul className="achievements">
      {ACHIEVEMENT_CARDS.map((card, index) => {
        const status = familyStatus(card.family, counts);
        const tier = status.badge?.tier;
        return (
          <li
            key={card.family}
            ref={(el) => { cardRefs.current[card.family] = el; }}
            className={`achievement-card${tier ? ` tier-${tier}` : ''}`}
            style={{ '--ach-i': index }}
          >
            <p className="achievement-card__title">{card.title}</p>
            <p className="achievement-card__tier">
              {tier ? `${TIER_LABELS[tier]} • ${card.count(status.value)}` : card.count(status.value)}
            </p>
            <div
              className="achievement-card__bar"
              role="progressbar"
              aria-label={status.next ? `Progress to ${status.next.tier}` : 'All tiers unlocked'}
              aria-valuemin={0}
              aria-valuemax={status.next?.threshold ?? status.value}
              aria-valuenow={Math.min(status.value, status.next?.threshold ?? status.value)}
            >
              <span style={{ width: `${Math.round(status.ratio * 100)}%` }} />
            </div>
            <p className="achievement-card__hint">
              {loading && !status.badge ? 'Checking your matches…' : status.hint}
            </p>
            {status.badge && (
              <span className="achievement-card__badge">
                <BadgeArt badge={status.badge} size={60} isNew={fresh.has(status.badge.id)} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
